import React, { useEffect, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { useCountry, useCtaHandler } from '../context/CountryContext';
import FadeInSection from './FadeInSection';
import SafeMotion from './SafeMotion';

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s };
}

export default function Countdown() {
  // ~103 days from now
  const target = React.useMemo(() => Date.now() + (103 * 86400000 + 23 * 3600000 + 57 * 60000 + 57 * 1000), []);
  const { d, h, m, s } = useCountdown(target);
  const { telegramUrl } = useCountry();
  const track = useCtaHandler();

  const cells = [
    { v: String(d).padStart(2, '0'), l: 'Days' },
    { v: String(h).padStart(2, '0'), l: 'Hours' },
    { v: String(m).padStart(2, '0'), l: 'Minutes' },
    { v: String(s).padStart(2, '0'), l: 'Seconds' },
  ];

  return (
    <section className="relative py-24 md:py-32">
      <div className="absolute inset-x-0 top-0 h-[380px] radial-glow-soft opacity-80 pointer-events-none" />
      <div className="relative max-w-4xl mx-auto px-6 text-center">
        <FadeInSection>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#ff7a2f]/30 bg-[#ff7a2f]/10 px-3.5 py-1.5 text-[11.5px] uppercase tracking-[0.18em] text-[#ffb27a]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#ff7a2f] animate-pulse" />
            October Phase Closing Soon
          </div>

          <h2 className="mt-6 font-display font-extrabold text-white text-4xl md:text-[56px] leading-[1.05] tracking-tight">
            Ready to claim
            <br />
            <span className="text-orange-gradient">your rewards?</span>
          </h2>
          <p className="mt-5 text-[#f2f2f2] text-[15px]">
            The registration window is now officially open. Start trading Silver, stack your lots, claim weekly cash bonuses, and secure your flight to Bali.
          </p>
        </FadeInSection>

        <div className="mt-10 grid grid-cols-4 gap-3 md:gap-4">
          {cells.map((c, i) => (
            <SafeMotion
              key={c.l}
              initial={{ filter: 'blur(10px)', opacity: 0, y: 20 }}
              whileInView={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1, margin: '0px 0px -50px 0px' }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: i * 0.08 }}
              className="card-dark p-4 md:p-6"
            >
              <div className="font-display font-extrabold text-3xl md:text-5xl text-orange-gradient tabular-nums">{c.v}</div>
              <div className="text-[#f2f2f2] text-[12px] mt-1">{c.l}</div>
            </SafeMotion>
          ))}
        </div>

        <FadeInSection delay={0.2}>
          <a href={telegramUrl} target="_blank" rel="noreferrer" onClick={() => track('countdown-cta')} className="btn-orange inline-flex items-center gap-2 px-6 py-3.5 text-[14.5px] mt-10">
            Register for the Bali Challenge Now <ArrowUpRight className="w-4 h-4" />
          </a>

          <div className="mt-4 text-[12px] text-[#f2f2f2]">Limited seats per weekly cohort. T&Cs apply.</div>
        </FadeInSection>
      </div>
    </section>
  );
}
